//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { RecurringRule } from '@hcengineering/calendar'
import type { Timestamp } from '@hcengineering/core'
import type { RecurrenceMode } from '.'

/**
 * @public
 */
export interface RecurrenceSpec {
  rules: RecurringRule[]
  startDate: Timestamp
  timeZone: string
}

const DAY = 24 * 60 * 60 * 1000
// Teto de segurança da busca pela próxima ocorrência
const SEARCH_CAP = 100 * 366 * DAY

/** Milissegundos a somar ao UTC para obter o relógio local em `timeZone` no instante `t`. */
export function tzOffset (t: Timestamp, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(new Date(t))
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  return asUtc - Math.floor(t / 1000) * 1000
}

const toLocal = (t: Timestamp, tz: string): Timestamp => t + tzOffset(t, tz)

function fromLocal (local: Timestamp, tz: string): Timestamp {
  // Duas passadas resolvem fusos com horário de verão
  const guess = local - tzOffset(local, tz)
  return local - tzOffset(guess, tz)
}

const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
const UNSUPPORTED_PARTS = ['bySecond', 'byMinute', 'byHour', 'byYearDay', 'byWeekNo', 'byMonth', 'bySetPos'] as const
// Maior intervalo em cada unidade: 100 anos, dentro do teto de busca
const MAX_INTERVAL = { DAILY: 36525, WEEKLY: 5218, MONTHLY: 1200 } as const

// Maior instante que Date representa: além dele toda data vira NaN
const MAX_TIME = 8.64e15

/**
 * Instante que o cálculo aceita: número finito dentro do alcance de Date. Texto, NaN e Infinity gravados
 * pela API deixariam as comparações sempre falsas, e a busca não terminaria (Ruling 39).
 * @public
 */
export function isValidTimestamp (value: unknown): value is Timestamp {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= MAX_TIME
}

/** A parte da regra fora do suporte, ou undefined se a regra é suportada. Nunca lança. */
function unsupportedPart (rule: RecurringRule): string | undefined {
  if (rule === null || typeof rule !== 'object' || Array.isArray(rule)) return 'rule is not an object'
  const { freq, byDay, byMonthDay, interval, count, endDate } = rule
  if (freq !== 'DAILY' && freq !== 'WEEKLY' && freq !== 'MONTHLY') return `freq ${freq}`
  const part = UNSUPPORTED_PARTS.find((p) => rule[p] != null)
  if (part !== undefined) return part
  if (byDay != null && !Array.isArray(byDay)) return 'byDay is not a list'
  if (byMonthDay != null && !Array.isArray(byMonthDay)) return 'byMonthDay is not a list'
  if (endDate != null && !isValidTimestamp(endDate)) return `endDate ${String(endDate)}`
  if (byDay != null && freq !== 'WEEKLY') return `byDay with freq ${freq}`
  if (byMonthDay != null && freq !== 'MONTHLY') return `byMonthDay with freq ${freq}`
  if (byDay?.length === 0) return 'empty byDay'
  const badDay = byDay?.find((d) => !WEEKDAYS.includes(d))
  if (badDay !== undefined) return `byDay ${badDay}`
  if (byMonthDay?.length === 0) return 'empty byMonthDay'
  const badMonthDay = byMonthDay?.find((d) => !Number.isInteger(d) || d < 1 || d > 31)
  if (badMonthDay !== undefined) return `byMonthDay ${badMonthDay}`
  if (interval != null && (!Number.isInteger(interval) || interval < 1)) return `interval ${interval}`
  if (interval != null && interval > MAX_INTERVAL[freq]) return `interval ${interval} above ${MAX_INTERVAL[freq]}`
  if (count != null && (!Number.isInteger(count) || count < 1)) return `count ${count}`
  return undefined
}

/**
 * Regras suportadas: DAILY; WEEKLY com ou sem byDay; MONTHLY com byMonthDay ou sem by-part (dia de startDate).
 * @public
 */
export function isSupportedRule (rule: RecurringRule): boolean {
  return unsupportedPart(rule) === undefined
}

function checkSpec (spec: RecurrenceSpec): void {
  if (!Array.isArray(spec.rules) || spec.rules.length === 0) throw new Error('Unsupported recurrence rule: no rules')
  if (!isValidTimestamp(spec.startDate)) { throw new Error(`Unsupported recurrence rule: startDate ${String(spec.startDate)}`) }
  for (const rule of spec.rules) {
    const part = unsupportedPart(rule)
    if (part !== undefined) throw new Error(`Unsupported recurrence rule: ${part}`)
  }
}

// Daqui em diante, "local" é um UTC falso: o relógio UTC do valor mostra o relógio do fuso da
// recorrência. Toda a aritmética usa métodos UTC, então o resultado não depende do fuso do processo.

const mod = (a: number, b: number): number => ((a % b) + b) % b

const monthIndex = (local: Timestamp): number => {
  const d = new Date(local)
  return d.getUTCFullYear() * 12 + d.getUTCMonth()
}

function daysInMonth (year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
}

/** Meia-noite local do dia `day` do mês `month` (pode passar de 11), limitado ao último dia do mês (spec T14). */
function monthDay (year: number, month: number, day: number): Timestamp {
  const first = new Date(Date.UTC(year, month, 1))
  const y = first.getUTCFullYear()
  const m = first.getUTCMonth()
  return Date.UTC(y, m, Math.min(day, daysInMonth(y, m)))
}

interface Period {
  first: Timestamp // meia-noite local do início do período
  days: Timestamp[] // meia-noite local de cada dia candidato, em ordem
}

interface Periods {
  at: (k: number) => Period
  indexOf: (local: Timestamp) => number // período que contém `local`
}

/**
 * Períodos da regra (dia, semana ou mês), a partir do dia de startDate, avançando `interval` por vez.
 * A semana começa no dia de startDate, como em generateRecurringValues, para tarefa e calendário concordarem.
 */
function periodsOf (rule: RecurringRule, startDay: Timestamp): Periods {
  const step = rule.interval ?? 1
  const start = new Date(startDay)
  switch (rule.freq) {
    case 'DAILY': {
      const len = step * DAY
      return {
        at: (k) => ({ first: startDay + k * len, days: [startDay + k * len] }),
        indexOf: (local) => Math.floor((local - startDay) / len)
      }
    }
    case 'WEEKLY': {
      const len = step * 7 * DAY
      const byDay = rule.byDay ?? [WEEKDAYS[start.getUTCDay()]]
      return {
        at: (k) => {
          const first = startDay + k * len
          const days: Timestamp[] = []
          for (let i = 0; i < 7; i++) {
            const day = first + i * DAY
            if (byDay.includes(WEEKDAYS[new Date(day).getUTCDay()])) days.push(day)
          }
          return { first, days }
        },
        indexOf: (local) => Math.floor((local - startDay) / len)
      }
    }
    case 'MONTHLY': {
      const byMonthDay = rule.byMonthDay ?? [start.getUTCDate()]
      const year = start.getUTCFullYear()
      const month = start.getUTCMonth()
      return {
        at: (k) => {
          const m = month + k * step
          const days = Array.from(new Set(byMonthDay.map((d) => monthDay(year, m, d)))).sort((a, b) => a - b)
          return { first: monthDay(year, m, 1), days }
        },
        indexOf: (local) => Math.floor((monthIndex(local) - monthIndex(startDay)) / step)
      }
    }
  }
  throw new Error(`Unsupported recurrence rule: freq ${rule.freq}`)
}

/**
 * Ocorrências locais da regra em [fromL, toL], em ordem, geradas sob demanda. count e endDate contam a
 * partir de startL (RFC 5545). Sem count, começa direto no período de fromL: o custo é proporcional ao
 * que se examina.
 */
function * localOccurrences (
  rule: RecurringRule,
  startL: Timestamp,
  fromL: Timestamp,
  toL: Timestamp
): Generator<Timestamp> {
  const timeOfDay = mod(startL, DAY)
  const periods = periodsOf(rule, startL - timeOfDay)
  const end = Math.min(toL, rule.endDate ?? Infinity)
  let n = 0
  for (let k = rule.count == null ? Math.max(0, periods.indexOf(fromL - timeOfDay)) : 0; ; k++) {
    const { first, days } = periods.at(k)
    // Além do maior instante que Date representa, as datas viram NaN e nenhuma comparação encerraria o laço
    if (!Number.isFinite(first) || first + timeOfDay > end) return
    for (const day of days) {
      const t = day + timeOfDay
      if (!Number.isFinite(t)) return
      if (t < startL) continue
      if (t > end) return
      if (rule.count != null && n >= rule.count) return
      n++
      if (t >= fromL) yield t
    }
  }
}

/** Regras com endDate já no relógio local. */
const localRules = (spec: RecurrenceSpec): RecurringRule[] =>
  spec.rules.map((rule) => (rule.endDate == null ? rule : { ...rule, endDate: toLocal(rule.endDate, spec.timeZone) }))

function between (spec: RecurrenceSpec, from: Timestamp, to: Timestamp): Timestamp[] {
  const tz = spec.timeZone
  const startL = toLocal(spec.startDate, tz)
  // Um dia de margem: com horário de verão o deslocamento muda entre from, to e cada ocorrência
  const fromL = toLocal(from, tz) - DAY
  const toL = toLocal(to, tz) + DAY
  const found = new Set<Timestamp>()
  for (const rule of localRules(spec)) {
    for (const local of localOccurrences(rule, startL, fromL, toL)) {
      const t = fromLocal(local, tz)
      if (t > from && t <= to) found.add(t)
    }
  }
  return Array.from(found).sort((a, b) => a - b)
}

function firstAfter (spec: RecurrenceSpec, after: Timestamp): Timestamp | null {
  const tz = spec.timeZone
  const startL = toLocal(spec.startDate, tz)
  const fromL = toLocal(after, tz) - DAY
  let best: Timestamp | null = null
  for (const rule of localRules(spec)) {
    for (const local of localOccurrences(rule, startL, fromL, fromL + SEARCH_CAP)) {
      const t = fromLocal(local, tz)
      if (t > after) {
        if (best === null || t < best) best = t
        break
      }
    }
  }
  return best
}

/**
 * Ocorrências em (from, to], ordenadas, sem duplicatas, calculadas no fuso `spec.timeZone`.
 * @public
 */
export function occurrencesBetween (spec: RecurrenceSpec, from: Timestamp, to: Timestamp): Timestamp[] {
  checkSpec(spec)
  return between(spec, from, to)
}

/**
 * Primeira ocorrência depois de `after`; null só quando a regra terminou (count esgotado ou endDate passou).
 * @public
 */
export function nextOccurrenceAfter (spec: RecurrenceSpec, after: Timestamp): Timestamp | null {
  checkSpec(spec)
  return firstAfter(spec, after)
}

/**
 * O que gerar agora e qual a próxima data.
 * - 'schedule': vencidas são as ocorrências reais da regra em [nextOccurrence, now]; gera só a mais recente
 *   (spec T9). Uma nextOccurrence guardada que não é ocorrência da regra atual nunca é gerada.
 * - 'afterCompletion': gera a nextOccurrence guardada quando vencida; next é null, porque a próxima data só
 *   existe quando a tarefa gerada for concluída (spec T15).
 * @public
 */
export function resolveDue (
  spec: RecurrenceSpec,
  nextOccurrence: Timestamp,
  now: Timestamp,
  mode: RecurrenceMode = 'schedule'
): { generate: Timestamp | null, next: Timestamp | null } {
  checkSpec(spec)
  if (mode === 'afterCompletion') return { generate: nextOccurrence <= now ? nextOccurrence : null, next: null }
  const due = between(spec, nextOccurrence - 1, now) // between é (from, to]; aqui [nextOccurrence, now]
  return { generate: due.length > 0 ? due[due.length - 1] : null, next: firstAfter(spec, now) }
}

/**
 * Próxima ocorrência ao retomar ou editar: a primeira depois de now, sem nada do passado.
 * @public
 */
export function resumeFrom (spec: RecurrenceSpec, now: Timestamp): Timestamp | null {
  checkSpec(spec)
  return firstAfter(spec, now)
}

/**
 * Modo após concluir (spec T15): conclusão mais o intervalo de rules[0], no fuso da recorrência,
 * na hora local de startDate.
 * @public
 */
export function nextAfterCompletion (spec: RecurrenceSpec, completedAt: Timestamp): Timestamp {
  checkSpec(spec)
  const rule = spec.rules[0]
  const n = rule.interval ?? 1
  const tz = spec.timeZone
  const timeOfDay = mod(toLocal(spec.startDate, tz), DAY)
  const done = new Date(toLocal(completedAt, tz))
  const year = done.getUTCFullYear()
  const month = done.getUTCMonth()
  const day = done.getUTCDate()
  let local: Timestamp
  switch (rule.freq) {
    case 'DAILY':
      local = Date.UTC(year, month, day + n)
      break
    case 'WEEKLY':
      local = Date.UTC(year, month, day + 7 * n)
      break
    case 'MONTHLY':
      local = monthDay(year, month + n, day)
      break
    default:
      throw new Error(`Unsupported recurrence rule: freq ${rule.freq}`)
  }
  return fromLocal(local + timeOfDay, tz)
}

/**
 * As próximas `count` ocorrências depois de `from`, para a prévia da interface (spec T1).
 * @public
 */
export function previewOccurrences (spec: RecurrenceSpec, from: Timestamp, count: number): Timestamp[] {
  checkSpec(spec)
  const out: Timestamp[] = []
  let after = from
  while (out.length < count) {
    const next = firstAfter(spec, after)
    if (next === null) break
    out.push(next)
    after = next
  }
  return out
}
